import { Button, DataButton, H5 } from "@sellar-io/components";
import { truncate } from "@sellar-io/common";
import { ReactElement, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon } from "../../../../../components";
import { Alert } from "../../../../../components/alert/Alert";
import { InputContainer, InputText } from "@sellar-io/components";
import { FilterPopover } from "../../../../../components/filterPopover/FilterPopover";
import { Tag } from "../../../../../components/tag/Tag";
import { SlideTransition } from "../../../../../components/transition/SlideTransition";
import { useStorefrontTagList } from "../../../../../hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useSupplierContainerTypesList } from "../../../../../hooks/api/v2/supplier/useSupplierContainerTypesList";
import {
  abvFilterType,
  AbvType,
  DietaryType,
  dietaryTypes,
} from "../../../../discovery/v2/DiscoveryV2SearchStore";

import {
  isSelectedTag,
  useStorefrontFilterStore,
} from "../../../StorefrontFilterStore";
import { useStockListContext } from "../../../useStockListContext";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

export const StorefrontFilters = ({
  supplierId,
}: {
  supplierId: number;
}): ReactElement => {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [clickOutside, setClickOutside] = useState<boolean>(true);
  const [mobileFilterActive, setMobileFilterActive] = useState(false);
  const {
    searchValue,
    setSearchValue,
    selectedTags,
    setSelectedTags,
    selectedAbvTypes,
    selectedDietaryTypes,
    setAbvType,
    setDietaryType,
    selectedGroupIds,
    setSelectedGroupIds,
  } = useStorefrontFilterStore(
    useShallow((state) => ({
      selectedTags: state.selectedTags,
      setSelectedTags: state.setSelectedTags,
      setSelectedGroupIds: state.setSelectedGroupIds,
      selectedGroupIds: state.selectedGroupIds,
      searchValue: state.searchValue,
      setSearchValue: state.setSearchValue,
      selectedAbvTypes: state.selectedAbvTypes,
      selectedDietaryTypes: state.selectedDietaryTypes,
      setDietaryType: state.setDietaryType,
      setAbvType: state.setAbvType,
    })),
  );
  const { categoryId } = useStockListContext();
  const containerTypesQuery = useSupplierContainerTypesList(
    { supplierId },
    {
      enabled: !!(supplierId && !categoryId),
    },
  );

  const tagsQuery = useStorefrontTagList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplierId,
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!supplierId,
    },
  );

  const getActiveFilterCount = () => {
    return (
      (selectedGroupIds.length > 0 ? 1 : 0) +
      (selectedAbvTypes.length > 0 ? 1 : 0) +
      (selectedDietaryTypes.length > 0 ? 1 : 0)
    );
  };

  const mappedSelectedGroups =
    tagsQuery.data?.pages[0]?.filter((tag) =>
      selectedGroupIds.includes(tag.id),
    ) || [];

  return (
    <div className={twMerge("flex flex-col overflow-hidden")}>
      <div
        className={twMerge(
          "flex w-full flex-col gap-2 border-t border-b border-zinc-300 bg-zinc-50 p-4",
          isDesktop && "rounded-lg border border-zinc-300",
        )}
      >
        <div className="flex flex-col-reverse gap-2 md:flex md:flex-row md:justify-between">
          {!isDesktop && <MarketFiltersAlert />}
          <div className="flex items-center gap-2 overflow-auto lg:flex-wrap">
            {containerTypesQuery.data?.pages[0]?.map((container) => (
              <Button
                color="slim"
                size="small"
                key={container}
                onClick={() => {
                  if (container && selectedTags.includes(container)) {
                    setSelectedTags(
                      selectedTags.filter(
                        (selectedTag) => selectedTag !== container,
                      ),
                    );
                  } else if (container && isSelectedTag(container)) {
                    setSelectedTags([...selectedTags, container]);
                  }
                }}
                selected={selectedTags.includes(container)}
                aria-pressed={selectedTags.includes(container)}
                className="capitalize shadow-xs"
                aria-label={`Filter by ${container}`}
              >
                {container === "bagInBox"
                  ? "Bag In Box"
                  : container.charAt(0).toUpperCase() + container.slice(1)}
              </Button>
            ))}
            {isDesktop && (
              <FilterPopover
                icon="FILTER_VARIANT"
                buttonText="More"
                compact={false}
                active={filterActive}
                toggleActive={setFilterActive}
                clickOutside={clickOutside}
              >
                <MoreFilters
                  onDismiss={() => setFilterActive(false)}
                  setClickOutside={(value?: boolean) =>
                    setClickOutside(value ?? !clickOutside)
                  }
                  supplierId={supplierId}
                />
              </FilterPopover>
            )}
          </div>
          <div className="flex flex-row gap-1">
            <InputContainer className={twMerge("w-full py-[0.4rem]")}>
              <InputText
                required={false}
                placeholder={`Search products...`}
                onChange={(e) => setSearchValue(e.currentTarget.value)}
                value={searchValue}
              />
            </InputContainer>
            {!isDesktop && (
              <DataButton
                onClick={() => setMobileFilterActive(!mobileFilterActive)}
                border={true}
                active={mobileFilterActive}
                className="relative min-h-[38.78px]"
              >
                <Icon icon="FILTER_VARIANT" />
                {getActiveFilterCount() > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black text-xs font-medium text-white">
                    {getActiveFilterCount()}
                  </span>
                )}
              </DataButton>
            )}
          </div>
        </div>
        {(selectedAbvTypes.length > 0 ||
          selectedDietaryTypes.length > 0 ||
          mappedSelectedGroups.length > 0) &&
          isDesktop && (
            <div className="flex w-full flex-row items-center gap-2 overflow-x-auto">
              <>
                {selectedAbvTypes.length > 0 &&
                  selectedAbvTypes.map((abvType, i) => (
                    <Tag
                      key={i}
                      onClick={() =>
                        setAbvType(
                          selectedAbvTypes.filter(
                            (abvTypeSelected) => abvTypeSelected !== abvType,
                          ),
                        )
                      }
                    >
                      {abvFilterType[abvType].name}
                    </Tag>
                  ))}
                {selectedDietaryTypes.length > 0 &&
                  selectedDietaryTypes.map((selectedDietaryType, i) => (
                    <Tag
                      key={i}
                      onClick={() =>
                        setDietaryType(
                          selectedDietaryTypes.filter(
                            (dietaryType) =>
                              dietaryType !== selectedDietaryType,
                          ),
                        )
                      }
                    >
                      {dietaryTypes[selectedDietaryType]}
                    </Tag>
                  ))}
                {mappedSelectedGroups.length > 0 &&
                  mappedSelectedGroups.map((selectedGroup, i) => (
                    <Tag
                      key={i}
                      onClick={() =>
                        setSelectedGroupIds(
                          selectedGroupIds.filter(
                            (id) => id !== selectedGroup.id,
                          ),
                        )
                      }
                    >
                      {selectedGroup.value}
                    </Tag>
                  ))}
              </>
              <MarketFiltersAlert />
            </div>
          )}
      </div>
      <SlideTransition
        show={mobileFilterActive}
        className={"w-full"}
        parentClass="w-full bg-white"
      >
        <MoreFilters
          onDismiss={() => setMobileFilterActive(false)}
          setClickOutside={(value?: boolean) =>
            setClickOutside(value ?? !clickOutside)
          }
          supplierId={supplierId}
        />
      </SlideTransition>
    </div>
  );
};

const MarketFiltersAlert = () => {
  const { filtersAppliedFromMarket, reset } = useStorefrontFilterStore(
    useShallow((state) => ({
      filtersAppliedFromMarket: state.filtersAppliedFromMarket,
      reset: state.reset,
    })),
  );
  return (
    <>
      {filtersAppliedFromMarket && (
        <Alert type={"information"} message={""} className={"flex-none p-2"}>
          <div className="flex flex-row items-center gap-2">
            <H5>Filters applied from the market </H5>
            <H5 className={"cursor-pointer text-blue-800"} onClick={reset}>
              Clear
            </H5>
          </div>
        </Alert>
      )}
    </>
  );
};

const MoreFilters = ({
  onDismiss,
  setClickOutside,
  supplierId,
}: {
  onDismiss: () => void;
  supplierId: number;
  setClickOutside?: (_value?: boolean) => void;
}) => {
  const state = useStorefrontFilterStore(useShallow((state) => state));

  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(
    state.selectedGroupIds,
  );

  const [selectedAbvTypes, setAbvTypes] = useState<AbvType[]>(
    state.selectedAbvTypes,
  );

  const [selectedDietaryTypes, setSelectedDietaryTypes] = useState<
    DietaryType[]
  >(state.selectedDietaryTypes);

  const tagsQuery = useStorefrontTagList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplierId,
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: !!supplierId,
    },
  );

  const onApply = (reset: boolean) => {
    if (reset) {
      state.reset();
    } else {
      state.setState({
        ...state,
        selectedGroupIds,
        selectedAbvTypes,
        selectedDietaryTypes,
      });
    }
    onDismiss();
  };

  const selectedTags = tagsQuery.data?.pages[0]
    ?.filter((tag) => selectedGroupIds.includes(tag.id))
    .map((tag) => tag.value)
    .join(", ");

  const canClearAll =
    !!selectedGroupIds.length ||
    !!selectedAbvTypes.length ||
    !!selectedDietaryTypes.length;
  return (
    <>
      <div className={twMerge("flex w-full flex-col gap-4 p-4")}>
        <span className="font-semibold">Filter by</span>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-10 items-center">
            <p className={"col-span-4 text-zinc-500"}>Product group</p>
            <div className="col-span-6 flex w-full flex-col">
              <Listbox
                value={selectedGroupIds}
                onChange={setSelectedGroupIds}
                multiple
              >
                <ListboxButton
                  onClick={() => {
                    if (setClickOutside) setClickOutside(false);
                  }}
                  className={
                    "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                  }
                >
                  {selectedTags?.length
                    ? truncate(selectedTags)
                    : "All Products"}
                  <Icon
                    icon="CHEVRON_DOWN"
                    size={0.8}
                    className="absolute right-2"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={
                    "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                  }
                  anchor="bottom start"
                >
                  <ListboxOption
                    key={"x1"}
                    value={false}
                    disabled={true}
                    className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                  >
                    Select multiple...
                  </ListboxOption>
                  {tagsQuery.data?.pages[0]?.map((tag, i) => (
                    <ListboxOption
                      key={i}
                      value={tag.id}
                      className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                    >
                      {selectedGroupIds.includes(tag.id) ? (
                        <Icon icon="CHECK" size={0.7} />
                      ) : (
                        <span className="w-[16.8px]" />
                      )}
                      {tag.value}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            </div>
          </div>
          <div className="grid grid-cols-10 items-center">
            <p className={"col-span-4 text-zinc-500"}>ABV</p>
            <div className="col-span-6 flex w-full flex-col">
              <Listbox value={selectedAbvTypes} onChange={setAbvTypes} multiple>
                <ListboxButton
                  onClick={() => {
                    if (setClickOutside) setClickOutside(false);
                  }}
                  className={
                    "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                  }
                >
                  {selectedAbvTypes.length
                    ? truncate(
                        selectedAbvTypes
                          .map(
                            (selectedType) => abvFilterType[selectedType].name,
                          )
                          .join(", "),
                      )
                    : "All %"}
                  <Icon
                    icon="CHEVRON_DOWN"
                    size={0.8}
                    className="absolute right-2"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={
                    "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                  }
                  anchor="bottom start"
                >
                  <ListboxOption
                    key={"x1"}
                    value={false}
                    disabled={true}
                    className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                  >
                    Select multiple...
                  </ListboxOption>
                  {Object.keys(abvFilterType).map((abvTypeKey, i) => (
                    <ListboxOption
                      key={i}
                      value={abvTypeKey}
                      className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                    >
                      {selectedAbvTypes.includes(abvTypeKey) ? (
                        <Icon icon="CHECK" size={0.7} />
                      ) : (
                        <span className="w-[16.8px]" />
                      )}
                      {abvFilterType[abvTypeKey as AbvType].name}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            </div>
          </div>
          <div className="grid grid-cols-10 items-center">
            <p className={"col-span-4 text-zinc-500"}>Dietary types</p>
            <div className="col-span-6 flex w-full flex-col">
              <Listbox
                value={selectedDietaryTypes}
                onChange={setSelectedDietaryTypes}
                multiple
              >
                <ListboxButton
                  onClick={() => {
                    if (setClickOutside) setClickOutside(false);
                  }}
                  className={
                    "relative flex w-full items-center overflow-hidden rounded-lg border border-solid bg-white px-4 py-2 pr-6 text-left text-sm font-medium text-ellipsis whitespace-nowrap shadow-xs"
                  }
                >
                  {selectedDietaryTypes.length
                    ? truncate(
                        selectedDietaryTypes
                          .map((type) => dietaryTypes[type])
                          .join(", "),
                      )
                    : "All dietary types"}
                  <Icon
                    icon="CHEVRON_DOWN"
                    size={0.8}
                    className="absolute right-2"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={
                    "mt-1 w-[190.3px] rounded-lg border border-solid bg-white p-2 px-4 text-left text-sm shadow-xs"
                  }
                  anchor="bottom start"
                >
                  <ListboxOption
                    key={"x1"}
                    value={false}
                    disabled={true}
                    className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 text-zinc-500 active:bg-zinc-50 data-focus:bg-blue-100"
                  >
                    Select multiple...
                  </ListboxOption>
                  {Object.keys(dietaryTypes).map((dietaryType, i) => (
                    <ListboxOption
                      key={i}
                      value={dietaryType}
                      className="flex w-full cursor-pointer items-center gap-1 rounded-sm py-2 active:bg-zinc-50 data-focus:bg-blue-100"
                    >
                      {selectedDietaryTypes.includes(dietaryType) ? (
                        <Icon icon="CHECK" size={0.7} />
                      ) : (
                        <span className="w-[16.8px]" />
                      )}
                      {dietaryTypes[dietaryType as DietaryType]}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Listbox>
            </div>
          </div>
          <div className="grid grid-cols-10 items-center">
            <p className={"col-span-4 text-zinc-500"}>In stock</p>
            <div className="col-span-6 flex w-full flex-col">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={!state.showOutOfStockProducts}
                  onChange={(e) =>
                    state.setShowOutOfStockProducts(!e.target.checked)
                  }
                />
              </label>
            </div>
          </div>
          <div className="flex flex-row gap-2">
            <DataButton
              onClick={() => onApply(false)}
              border={true}
              disabled={!canClearAll}
            >
              Apply
            </DataButton>
            {canClearAll && (
              <DataButton onClick={() => onApply(true)}>Clear all</DataButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
